import { promises as fs } from 'fs';
import path from 'path';
import matter from 'gray-matter';
import type {
    IDE,
    ContentType,
    ContextItem,
    IDEContext,
    ProjectContext,
    ContextStats,
} from '../types/context.types.js';
import { IDE_CONFIGS, getAllIDEs } from '../constants/ide-configs.js';

/**
 * Service for scanning and parsing context files
 */
export class ContextScannerService {
    private projectPath: string;

    constructor(projectPath: string = process.cwd()) {
        this.projectPath = projectPath;
    }

    /**
     * Scan all IDEs for context
     */
    async scanAll(): Promise<ProjectContext> {
        const ides = await this.detectIDEs();
        const ideContexts: IDEContext[] = [];

        for (const ide of ides) {
            try {
                const context = await this.scanIDE(ide);
                if (context.items.length > 0) {
                    ideContexts.push(context);
                }
            } catch (error) {
                // Skip IDEs that fail to scan
                console.error(`Failed to scan ${ide}:`, error);
            }
        }

        const totalItems = ideContexts.reduce((sum, ctx) => sum + ctx.items.length, 0);

        return {
            projectPath: this.projectPath,
            ides: ideContexts,
            totalItems,
            scanTime: new Date(),
        };
    }

    /**
     * Scan specific IDE
     */
    async scanIDE(ide: IDE): Promise<IDEContext> {
        const config = IDE_CONFIGS[ide];
        const items: ContextItem[] = [];

        // Scan each content type
        for (const [type, relativePath] of Object.entries(config.contentPaths)) {
            if (!relativePath) continue;

            const contentType = type as ContentType;
            try {
                const typeItems = await this.scanType(ide, contentType);
                items.push(...typeItems);
            } catch (error) {
                // Skip types that fail to scan (directory doesn't exist, etc.)
            }
        }

        const stats = this.calculateStats(items);

        return {
            ide,
            config,
            items,
            stats,
            scanTime: new Date(),
        };
    }

    /**
     * Scan specific content type
     */
    async scanType(ide: IDE, type: ContentType): Promise<ContextItem[]> {
        const config = IDE_CONFIGS[ide];
        const relativePath = config.contentPaths[type];
        if (!relativePath) return [];

        // OpenCode rules: single AGENTS.md file at project root
        if (ide === 'opencode' && type === 'rules') {
            return this.scanOpenCodeRules();
        }

        let dirPath: string;

        // For jai1, check both .jai1 and packages/jai1-framework
        if (ide === 'jai1') {
            const jai1Path = path.join(this.projectPath, '.jai1', relativePath);
            const frameworkPath = path.join(this.projectPath, 'packages/jai1-framework', relativePath);

            // Use whichever path exists
            if (await this.pathExists(jai1Path)) {
                dirPath = jai1Path;
            } else if (await this.pathExists(frameworkPath)) {
                dirPath = frameworkPath;
            } else {
                return [];
            }
        } else {
            dirPath = path.join(this.projectPath, config.basePath, relativePath);

            // Check if directory exists
            try {
                await fs.access(dirPath);
            } catch {
                return [];
            }
        }

        const extensions = config.fileExtensions[type] || [];
        const items: ContextItem[] = [];

        // Special handling for skills (folders with SKILL.md)
        if (type === 'skills') {
            const skillItems = await this.scanSkills(dirPath, ide);
            items.push(...skillItems);
        } else {
            // Regular files
            const files = await fs.readdir(dirPath);

            for (const file of files) {
                const filepath = path.join(dirPath, file);
                const stat = await fs.stat(filepath);

                if (!stat.isFile()) continue;

                // Check if file matches extensions
                const matchesExtension = extensions.some(ext => file.endsWith(ext));
                if (!matchesExtension) continue;

                try {
                    const item = await this.parseContextItem(filepath, ide, type);
                    items.push(item);
                } catch (error) {
                    console.error(`Failed to parse ${filepath}:`, error);
                }
            }
        }

        return items;
    }

    /**
     * Scan skills (folders with SKILL.md)
     */
    private async scanSkills(skillsDir: string, ide: IDE): Promise<ContextItem[]> {
        const items: ContextItem[] = [];

        try {
            const entries = await fs.readdir(skillsDir, { withFileTypes: true });

            for (const entry of entries) {
                if (!entry.isDirectory()) continue;

                const skillPath = path.join(skillsDir, entry.name);
                const skillFilePath = path.join(skillPath, 'SKILL.md');

                // Check if SKILL.md exists
                try {
                    await fs.access(skillFilePath);
                } catch {
                    continue;
                }

                // Parse SKILL.md
                const item = await this.parseContextItem(skillFilePath, ide, 'skills');

                // Check for additional folders
                item.hasScripts = await this.pathExists(path.join(skillPath, 'scripts'));
                item.hasReferences = await this.pathExists(path.join(skillPath, 'references'));
                item.hasAssets = await this.pathExists(path.join(skillPath, 'assets'));

                items.push(item);
            }
        } catch (error) {
            console.error(`Failed to scan skills:`, error);
        }

        return items;
    }

    /**
     * Scan OpenCode rules (AGENTS.md at project root)
     */
    private async scanOpenCodeRules(): Promise<ContextItem[]> {
        const agentsPath = path.join(this.projectPath, 'AGENTS.md');

        if (!await this.pathExists(agentsPath)) {
            return [];
        }

        try {
            const item = await this.parseContextItem(agentsPath, 'opencode', 'rules');
            item.alwaysApply = true; // AGENTS.md is always active
            return [item];
        } catch {
            return [];
        }
    }

    /**
     * Parse a context item from file
     */
    private async parseContextItem(
        filepath: string,
        ide: IDE,
        type: ContentType
    ): Promise<ContextItem> {
        const content = await fs.readFile(filepath, 'utf-8');
        const stat = await fs.stat(filepath);

        // Parse frontmatter
        let frontmatter: Record<string, unknown> = {};
        let bodyContent: string = content;
        try {
            const parsed = matter(content);
            frontmatter = parsed.data;
            bodyContent = parsed.content;
        } catch (e) {
            // If YAML parsing fails, treat as no frontmatter
            console.warn(`⚠️  Invalid YAML frontmatter in ${filepath}, skipping frontmatter parsing`);
        }

        // Extract description
        const config = IDE_CONFIGS[ide];
        const description = frontmatter[config.frontmatterSchema.descriptionField] as string | undefined;

        // Extract globs/trigger info
        let globs: string[] | undefined;
        let alwaysApply: boolean | undefined;

        if (config.frontmatterSchema.alwaysApplyField) {
            const alwaysField = frontmatter[config.frontmatterSchema.alwaysApplyField];
            alwaysApply = alwaysField === true || alwaysField === 'always' || alwaysField === 'always_on';
        }

        const triggerValue = frontmatter[config.frontmatterSchema.triggerField];
        if (typeof triggerValue === 'string') {
            globs = [triggerValue];
        } else if (Array.isArray(triggerValue)) {
            globs = triggerValue;
        }

        // Extract preview lines
        const { previewLines, lineCount } = this.extractPreview(bodyContent, 20);

        // Generate ID
        const relativePath = path.relative(this.projectPath, filepath);
        const id = `${ide}-${type}-${path.basename(filepath, path.extname(filepath))}`;

        // Extract name
        const name = (frontmatter.name as string) || path.basename(filepath, path.extname(filepath));

        return {
            id,
            ide,
            type,
            name,
            filepath,
            relativePath,
            frontmatter,
            description,
            globs,
            alwaysApply,
            model: frontmatter.model as string | undefined,
            fileSize: stat.size,
            modifiedAt: stat.mtime,
            previewLines,
            lineCount,
        };
    }

    /**
     * Extract preview lines (after frontmatter)
     */
    private extractPreview(content: string, maxLines: number = 20): { previewLines: string[]; lineCount: number } {
        const lines = content.split('\n');
        const previewLines = lines.slice(0, maxLines);
        const lineCount = lines.length;

        return { previewLines, lineCount };
    }

    /**
     * Detect available IDEs in project
     */
    async detectIDEs(): Promise<IDE[]> {
        const ides: IDE[] = [];

        for (const ide of getAllIDEs()) {
            const config = IDE_CONFIGS[ide];

            // For jai1, check both .jai1 and packages/jai1-framework
            if (ide === 'jai1') {
                const jai1Path = path.join(this.projectPath, '.jai1');
                const frameworkPath = path.join(this.projectPath, 'packages/jai1-framework');

                if (await this.pathExists(jai1Path) || await this.pathExists(frameworkPath)) {
                    ides.push(ide);
                }
            } else if (ide === 'opencode') {
                // OpenCode: detect via AGENTS.md or .opencode/ directory
                const agentsPath = path.join(this.projectPath, 'AGENTS.md');
                const opencodePath = path.join(this.projectPath, '.opencode');

                if (await this.pathExists(agentsPath) || await this.pathExists(opencodePath)) {
                    ides.push(ide);
                }
            } else {
                const idePath = path.join(this.projectPath, config.basePath);
                if (await this.pathExists(idePath)) {
                    ides.push(ide);
                }
            }
        }

        return ides;
    }

    /**
     * Check if path exists
     */
    private async pathExists(filepath: string): Promise<boolean> {
        try {
            await fs.access(filepath);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Calculate statistics from items
     */
    private calculateStats(items: ContextItem[]): ContextStats {
        const byType: Partial<Record<ContentType, number>> = {};

        for (const item of items) {
            byType[item.type] = (byType[item.type] || 0) + 1;
        }

        const totalSize = items.reduce((sum, item) => sum + item.fileSize, 0);

        const lastModified = items.length > 0
            ? items.reduce((latest, item) =>
                item.modifiedAt > latest ? item.modifiedAt : latest,
                items[0]!.modifiedAt
            )
            : null;

        return {
            totalItems: items.length,
            byType,
            totalSize,
            lastModified,
        };
    }
}
